import { AuditEvent, ListAuditTrailParams, listAuditTrails } from '@brands/services/audit_trails/listAuditTrails';
import uniqBy from 'lodash/uniqBy';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { Meta } from '../services/communication/types/Meta';
import { displayErrorDetails } from '../Utils/displayError';

interface IUseConversations {
  auditTrailList: AuditEvent[];
  reloadAll: () => Promise<void>;
  loadNextPage: () => Promise<void>;
  loadPreviousPage: () => Promise<void>;
  dataLoading: boolean;
  loading: boolean;
}

export default function useAuditTrail(
  params: Pick<
    ListAuditTrailParams,
    'actor_name' | 'date_from' | 'date_to' | 'user_name' | 'user_role' | 'search' | 'limit'
  >,
): IUseConversations {
  const [auditTrailList, setAuditTrailList] = useState<AuditEvent[]>([]);
  const [dataLoading, setDataLoading] = useState<boolean>(false);
  const [firstPageMeta, setFirstPageMeta] = useState<Meta | null>(null);
  const [lastPageMeta, setLastPageMeta] = useState<Meta | null>(null);
  const [loading, setLoading] = useState(false);

  const queryString = useMemo(
    () =>
      Object.keys(params)
        .map((key) => {
          const value = params[key as keyof typeof params];
          if (Array.isArray(value)) {
            return value.map((v) => `${key}=${encodeURIComponent(v)}`).join('&');
          }
          return `${key}=${encodeURIComponent(value as string | number | boolean)}`;
        })
        .join('&'),
    [params],
  );

  const loadAll = useCallback(async (): Promise<void> => {
    setDataLoading(true);
    try {
      const auditTrails = await listAuditTrails(queryString);
      setAuditTrailList(auditTrails.nodes);
      setFirstPageMeta(auditTrails.meta);
      setLastPageMeta(auditTrails.meta);
    } catch (error: unknown) {
      displayErrorDetails(error);
    }
    setDataLoading(false);
  }, [queryString]);

  const reloadAll = useCallback(async (): Promise<void> => {
    setDataLoading(true);
    try {
      const auditTrails = await listAuditTrails(queryString);
      setAuditTrailList(auditTrails.nodes);
      setFirstPageMeta(auditTrails.meta);
      setLastPageMeta(auditTrails.meta);
    } catch (error: unknown) {
      displayErrorDetails(error);
    }
    setDataLoading(false);
  }, [queryString]);

  const loadNextPage = async (): Promise<void> => {
    setLoading(true);
    if (!lastPageMeta?.has_next_page) {
      return;
    }
    try {
      const { meta, nodes } = await listAuditTrails(
        `${queryString}&${`cursor=${lastPageMeta.end_cursor}`}&cursor_direction=${`next`}`,
      );
      setLastPageMeta(meta);
      if (!firstPageMeta) {
        setFirstPageMeta(meta);
      }
      setAuditTrailList((prevCases) => uniqBy([...prevCases, ...nodes], 'id'));
    } catch (error: unknown) {
      displayErrorDetails(error);
    }
    setLoading(false);
  };

  const loadPreviousPage = async (): Promise<void> => {
    if (!firstPageMeta?.has_previous_page) {
      return;
    }
    try {
      const { meta, nodes } = await listAuditTrails(
        `${queryString}&cursor=${firstPageMeta.start_cursor}&cursor_direction=${`previous`}`,
      );
      setFirstPageMeta(meta);
      if (!lastPageMeta) {
        setLastPageMeta(meta);
      }
      setAuditTrailList((prevCases) => uniqBy([...nodes, ...prevCases], 'id'));
    } catch (error: unknown) {
      displayErrorDetails(error);
    }
  };

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  return {
    auditTrailList,
    reloadAll,
    loadNextPage,
    loadPreviousPage,
    dataLoading,
    loading,
  };
}
